import { z } from 'zod';

import { uuidSchema } from './general';
import type { FundListing } from './models';
// Imported from their defining modules rather than the `./models` barrel: these are
// evaluated at module init, and `./inputs-forms` is re-exported before `./models`.
import { entityTypeSchema, portfolioTradeTypeSchema } from './models/misc';
import { physicalAddressSchema, userIdentitySchema } from './models/user';
import type { Address } from './web3';
import { addressSchema, bigIntSchema } from './web3';

export const createFundDetailsInputSchema = z.object({
  name: z.string().trim().min(1, 'Please enter a name for your new fund'),
  chainId: z.number(),
  referralSource: z
    .string()
    .trim()
    .max(99, 'Please enter a referral source less than or equal to 99 characters')
    .optional(),
});
export const createFundAdvisorInputSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().email().trim(),
  address: physicalAddressSchema,
});

export type CreateFundDetailsInput = z.infer<typeof createFundDetailsInputSchema>;
export type CreateFundAdvisorInput = z.infer<typeof createFundAdvisorInputSchema>;

export type CreateFundInput = {
  fundInput: CreateFundDetailsInput;
  advisor: CreateFundAdvisorInput;
  fundSalt?: string;
  deploymentTransactionHash?: Address;
};

export type FundSettingsInput = Pick<FundListing, 'description' | 'name' | 'type'>;

const assembledTradeTransactionDataSchema = z.object({
  portfolioAddress: addressSchema,
  /** A USDC value on a BUY trade, A number of shares on a SELL trade */
  amount: bigIntSchema,
  callData: addressSchema,
  tradeType: portfolioTradeTypeSchema,
  chainId: z.number(),
  priceImpact: z.number().nullish(),
});
const assembledTradeEstimationsSchema = z.object({
  /** A USDC value */
  previousPositionUsdc: bigIntSchema,
  /** A USDC value */
  newEstimatedPositionUsdc: bigIntSchema,
  /** A USDC value */
  minimumNewEstimatedPositionUsdc: bigIntSchema.nullish(),
  /** A USDC value */
  estimatedGrantableBalanceChangeUsdc: bigIntSchema,
  /** A USDC value */
  minimumGrantableBalanceChangeUsdc: bigIntSchema.nullish(),
  /** A USDC value */
  estimatedNewGrantableBalanceUsdc: bigIntSchema,
  /** A USDC value */
  minimumNewGrantableBalanceUsdc: bigIntSchema.nullish(),
  /** A USDC value */
  estimatedFee: bigIntSchema,
});
export const assembledTradeResponseSchema = z.object({
  entityId: uuidSchema,
  entityType: entityTypeSchema,
  entityAddress: addressSchema,
  portfolioId: uuidSchema,
  tradeData: assembledTradeTransactionDataSchema,
  estimations: assembledTradeEstimationsSchema,
});
export type AssembledTradeResponse = z.infer<typeof assembledTradeResponseSchema>;

export const createDonationInputSchema = z
  .object({
    subprojectId: uuidSchema.optional(),
    shareMyEmail: z.boolean(),
    recommendationId: uuidSchema.optional(),
  })
  .and(
    z.union([
      z.object({ taxReceipt: z.literal(false), donorIdentity: z.undefined() }),
      z.object({ taxReceipt: z.literal(true), donorIdentity: userIdentitySchema }),
    ]),
  );
export type CreateDonationInput = z.infer<typeof createDonationInputSchema>;
